// A comet's coma, jets, ion tail and dust tail as one analytic volume.
//
// One box in the comet's frame (+x along the orbital motion, +y the orbit
// normal, +z away from the Sun, the nucleus near the origin) is drawn with a
// shader that integrates every component along the view ray in closed form,
// so nothing is marched in screen space and nothing shimmers as the camera
// moves:
//
// - Coma: gas falls off as 1/r^2 (steady outflow), whose column is ~1/rho on
//   the sky. The truncated 1/(r^2 + a^2) column through a sphere is exact
//   (two atans), from outside or from inside the coma. The gas is C2 Swan
//   green, the dust coma is sunlight held inside the fountain paraboloid the
//   grains turn round on (turnaround distance v^2 / 2 beta g).
// - Jets: a few dust cones from the day side, as tubes integrated across.
// - Ion tail: a CO+ blue tube along the anti-solar axis, integrated across
//   in closed form at the ray's closest approach (a Gaussian in the cross
//   section), with streamers, knots carried down the tail and now and then
//   a disconnection event.
// - Dust tail: a sheet in the orbit plane mapped from the syndyne/synchrone
//   picture. In the comet's rotating frame a grain released at rest feels
//   beta g away from the Sun and the Coriolis term -2 w x v, so a grain of
//   size parameter beta released tau ago sits at z = beta g tau^2 / 2,
//   x = -w beta g tau^3 / 3. Synchrones (one tau) are straight lines at
//   x/z = -2 w tau / 3; syndynes (one beta) bend back as z^(3/2). Each point
//   of the plane maps back to its (tau, beta), the density is the emission
//   history times the size distribution over the Jacobian, and striae are
//   bands in tau that do not meet at the nucleus. Across the sheet the
//   thickness grows with tau (ejection speed), integrated exactly per step
//   with the normal CDF.
// - Light: dust takes a two-term Henyey-Greenstein phase function close to
//   the Schleicher-Marcus comet curve (a gentle backscatter rise, a minimum
//   near 90 degrees, a strong forward surge past 150), so the dust brightens
//   sharply at high phase. Gas is fluorescence, isotropic.
//
// Detail that would fall under a pixel is faded by the pixel's footprint
// (prefiltered), so the far view is the mean and stays still.
import * as THREE from 'three';
import { OKLAB_MIX_GLSL } from '../shaders/oklab.js';

const VERT = /* glsl */`
varying vec3 vLocal;
void main() {
  vLocal = position;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FRAG = /* glsl */`
uniform vec3 uCam;
uniform vec3 uBoxMin;
uniform vec3 uBoxMax;
uniform vec3 uNucleus;
uniform float uNucR;
uniform float uSunDist;
uniform float uTime;
uniform float uPixelAngle;
uniform float uSeed;
// coma
uniform float uGasA;
uniform float uGasR;
uniform float uGasGain;
uniform float uDustComaA;
uniform float uDustComaR;
uniform float uDustComaGain;
uniform float uHood;
uniform float uJetGain;
uniform vec3 uJetDir0;
uniform vec3 uJetDir1;
uniform vec3 uJetDir2;
uniform float uJetLen;
// ion tail
uniform float uIonLen;
uniform float uIonW0;
uniform float uIonW1;
uniform float uIonGain;
// dust tail
uniform float uDustLen;
uniform float uDustK;
uniform float uDustThick0;
uniform float uDustThick1;
uniform float uDustGain;
uniform float uStriae;
// colours (linear)
uniform vec3 uGasColor;
uniform vec3 uIonColor;
uniform vec3 uDustColor;
uniform vec3 uDustHeadColor;
uniform float uExposure;

varying vec3 vLocal;

${OKLAB_MIX_GLSL}

float ckHash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
float ckHash11(float p) {
  p = fract(p * 0.1031);
  p *= p + 33.33;
  p *= p + p;
  return fract(p);
}
float ckNoise1(float x) {
  float i = floor(x);
  float f = fract(x);
  float u = f * f * (3.0 - 2.0 * f);
  return mix(ckHash11(i), ckHash11(i + 1.0), u);
}
float ckNoise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = ckHash12(i);
  float b = ckHash12(i + vec2(1.0, 0.0));
  float c = ckHash12(i + vec2(0.0, 1.0));
  float d = ckHash12(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}
// fbm whose octaves fade out once they fall under the pixel footprint:
// lod is the number of features per pixel at the base octave.
float ckFbm2(vec2 p, float lod) {
  float v = 0.0;
  float amp = 0.5;
  float norm = 0.0;
  float w = 1.0;
  for (int i = 0; i < 4; i++) {
    float keep = 1.0 - smoothstep(0.15, 0.5, lod);
    v += amp * mix(0.5, ckNoise2(p), keep);
    norm += amp;
    p = p * 2.03 + vec2(17.3, 5.1);
    amp *= 0.5;
    lod *= 2.03;
  }
  return v / norm;
}
float ckFbm1(float x, float lod) {
  float v = 0.0;
  float amp = 0.5;
  float norm = 0.0;
  for (int i = 0; i < 3; i++) {
    float keep = 1.0 - smoothstep(0.15, 0.5, lod);
    v += amp * mix(0.5, ckNoise1(x), keep);
    norm += amp;
    x = x * 2.11 + 3.7;
    amp *= 0.5;
    lod *= 2.11;
  }
  return v / norm;
}
// Standard normal CDF.
float ckPhi(float x) {
  x = clamp(x, -6.0, 6.0);
  return 0.5 + 0.5 * tanh(0.7978845608 * (x + 0.044715 * x * x * x));
}
float ckHG(float g, float c) {
  float g2 = g * g;
  return (1.0 - g2) / pow(max(1.0 + g2 - 2.0 * g * c, 1e-4), 1.5);
}
// Comet dust phase function, normalised to 1 at 90 degrees phase.
float ckDustPhase(float cosScatter) {
  return (0.4 * ckHG(0.8, cosScatter) + 0.6 * ckHG(-0.25, cosScatter)) / 0.58;
}

// Column of the density 1/(r^2 + a^2) - 1/(R^2 + a^2) inside a sphere of
// radius R about c, between ray distances t0 and t1. tc and b are the ray's
// closest approach to c.
float ckComa(vec3 ro, vec3 rd, vec3 c, float a, float R, float t0, float t1, out float tc) {
  vec3 oc = ro - c;
  tc = -dot(oc, rd);
  float b2 = max(dot(oc, oc) - tc * tc, 0.0);
  if (b2 >= R * R) return 0.0;
  float h = sqrt(R * R - b2);
  float s0 = max(t0, tc - h);
  float s1 = min(t1, tc + h);
  if (s1 <= s0) return 0.0;
  float k = sqrt(b2 + a * a);
  float col = (atan((s1 - tc) / k) - atan((s0 - tc) / k)) / k;
  return max(col - (s1 - s0) / (R * R + a * a), 0.0);
}

// Column across a Gaussian tube along the segment p0 + dir * s, s in [0, len],
// width sigma(s) = w0 + w1 * s / len. Returns the column; sOut the axial
// position of the closest approach, uOut the signed lateral offset.
float ckTube(vec3 ro, vec3 rd, vec3 p0, vec3 dir, float len, float w0, float w1,
             float t0, float t1, float minSin, out float sOut, out float uOut, out float sigOut) {
  vec3 w = ro - p0;
  float b = dot(rd, dir);
  float sa2 = max(1.0 - b * b, minSin * minSin);
  float d = dot(rd, w);
  float e = dot(dir, w);
  float tA = (b * e - d) / sa2;
  float sA = (e - b * d) / sa2;
  sOut = sA;
  float sc = clamp(sA, 0.0, len);
  vec3 diff = w + rd * tA - dir * sc;
  vec3 side = cross(rd, dir);
  float sl = length(side);
  side = sl > 1e-5 ? side / sl : vec3(1.0, 0.0, 0.0);
  uOut = dot(diff, side);
  float dmin2 = dot(diff, diff);
  float sig = w0 + w1 * clamp(sc / max(len, 1e-4), 0.0, 1.0);
  sigOut = sig;
  float sa = sqrt(sa2);
  float across = exp(-0.5 * dmin2 / (sig * sig));
  // Fraction of the Gaussian crossing that lies between t0 and t1.
  float frac = ckPhi((t1 - tA) * sa / sig) - ckPhi((t0 - tA) * sa / sig);
  return across * frac * sig * 2.5066 / sa;
}

vec2 ckBox(vec3 ro, vec3 rd, vec3 bmin, vec3 bmax) {
  vec3 inv = 1.0 / rd;
  vec3 ta = (bmin - ro) * inv;
  vec3 tb = (bmax - ro) * inv;
  vec3 tmn = min(ta, tb);
  vec3 tmx = max(ta, tb);
  return vec2(max(max(tmn.x, tmn.y), tmn.z), min(min(tmx.x, tmx.y), tmx.z));
}

void main() {
  vec3 ro = uCam;
  vec3 rd = normalize(vLocal - uCam);
  // Avoid exact zeros in the slab maths.
  rd = normalize(rd + vec3(1e-7, 1e-7, 1e-7));
  vec2 tb = ckBox(ro, rd, uBoxMin, uBoxMax);
  float t0 = max(tb.x, 0.0);
  float t1 = tb.y;
  if (t1 <= t0) discard;

  // The nucleus hides what lies behind it.
  vec3 on = ro - uNucleus;
  float bn = dot(on, rd);
  float cn = dot(on, on) - uNucR * uNucR;
  float hn = bn * bn - cn;
  if (hn > 0.0) {
    float th = -bn - sqrt(hn);
    if (th > 0.0) t1 = min(t1, th);
  }

  vec3 sunPos = vec3(0.0, 0.0, -uSunDist);
  float tNuc = max(dot(uNucleus - ro, rd), t0);
  // Footprint of a pixel at the nucleus' distance, scene units.
  float footHead = max(tNuc, 1e-3) * uPixelAngle;

  // ---- Coma ----
  float tcG;
  float gasCol = ckComa(ro, rd, uNucleus, uGasA, uGasR, t0, t1, tcG) * uGasA;
  float tcD;
  float dustCol = ckComa(ro, rd, uNucleus, uDustComaA, uDustComaR, t0, t1, tcD) * uDustComaA;
  // Fountain paraboloid: grains thrown sunward turn round at uHood and are
  // swept back, so the dust coma is bounded sunward by z' = -uHood + rho^2/(4 uHood).
  vec3 pc = ro + rd * clamp(tcD, t0, t1) - uNucleus;
  float rho2 = dot(pc.xy, pc.xy);
  float hoodS = (pc.z + uHood - rho2 / (4.0 * uHood)) / uHood;
  float hood = smoothstep(-0.35, 0.6, hoodS);
  vec3 sunDirC = normalize(sunPos - uNucleus);
  float phaseC = ckDustPhase(dot(sunDirC, rd));
  // A slight sunward brightening of the gas (production is from the day side).
  float gasAsym = 1.0 + 0.25 * clamp(-dot(normalize(pc + vec3(1e-4)), vec3(0.0, 0.0, 1.0)), -1.0, 1.0);
  vec3 col = uGasColor * gasCol * uGasGain * gasAsym;
  vec3 dustLight = uDustHeadColor * dustCol * uDustComaGain * hood * phaseC;

  // ---- Jets ----
  // Narrow dust cones from active areas on the day side, widening as they
  // rise and fading by the hood where radiation pressure turns them back.
  {
    float sJ; float uJ; float sigJ;
    float jets = 0.0;
    for (int k = 0; k < 3; k++) {
      vec3 jd = k == 0 ? uJetDir0 : (k == 1 ? uJetDir1 : uJetDir2);
      float jl = uJetLen * (k == 0 ? 1.0 : (k == 1 ? 0.8 : 0.6));
      float jw = k == 0 ? 1.0 : (k == 1 ? 0.7 : 0.5);
      float j = ckTube(ro, rd, uNucleus + jd * uNucR * 0.8, jd, jl, uNucR * 0.18, jl * 0.22, t0, t1, 0.08, sJ, uJ, sigJ);
      float sc = clamp(sJ, 0.0, jl);
      float fade = (1.0 - smoothstep(0.3 * jl, jl, sc)) * smoothstep(-0.2 * jl, 0.05 * jl, sJ);
      // Surface brightness falls as the cone widens (flux conserved).
      jets += jw * j * fade / sigJ;
    }
    dustLight += uDustHeadColor * jets * uJetGain * phaseC;
  }

  // ---- Ion tail ----
  {
    float sI; float uI; float sigI;
    vec3 axis = vec3(0.0, 0.0, 1.0);
    float ion = ckTube(ro, rd, uNucleus, axis, uIonLen, uIonW0, uIonW1 - uIonW0, t0, t1, 0.12, sI, uI, sigI);
    float sn = clamp(sI / uIonLen, 0.0, 1.2);
    float along = smoothstep(-0.01, 0.03, sn) * exp(-sn * 2.2) * (1.0 - smoothstep(0.75, 1.05, sn));
    float footI = max(dot(uNucleus + axis * max(sI, 0.0) - ro, rd), t0) * uPixelAngle;
    // Streamers: structure stretched along the tail, advected outward.
    float lodS = footI / max(sigI * 0.5, 1e-4);
    float flow = uTime * 0.035;
    float str = ckFbm2(vec2(uI / sigI * 1.7 + uSeed, sn * 5.0 - flow * 5.0), lodS);
    float streamers = mix(1.0, 0.25 + 1.75 * str * str * 1.6, 0.85);
    // Knots: brighter clumps carried down the tail.
    float lodK = footI / max(uIonLen * 0.06, 1e-4);
    float knots = mix(1.0, 0.45 + 1.1 * ckFbm1(sn * 9.0 - flow * 9.0 + uSeed * 3.1, lodK), 0.9);
    // Disconnection: every so often the tail is cut near the head and the
    // severed part drifts away while a new tail grows behind it.
    float cyc = uTime / 70.0 + uSeed;
    float cycId = floor(cyc);
    float ph = fract(cyc);
    float hasEvent = step(0.45, ckHash11(cycId * 7.13 + 1.7));
    float gapS = ph * 1.3;
    float gap = exp(-pow((sn - gapS) / 0.045, 2.0)) * hasEvent * smoothstep(0.0, 0.08, ph) * (1.0 - smoothstep(0.75, 1.0, ph));
    float regrow = mix(1.0, smoothstep(0.0, 0.5, ph), hasEvent * step(sn, gapS) * (1.0 - smoothstep(0.75, 1.0, ph)) * 0.6);
    float ionB = ion * along * streamers * knots * (1.0 - 0.85 * gap) * mix(1.0, regrow, 1.0);
    // The tail's column is brightest when foreshortened; cap the gain.
    col += uIonColor * ionB * uIonGain / max(sigI, 1e-4) * uIonW0;
  }

  // ---- Dust tail ----
  {
    float Y = 3.0 * (uDustThick0 + uDustThick1);
    float sy0 = t0;
    float sy1 = t1;
    if (abs(rd.y) > 1e-6) {
      float ya = (-Y - ro.y) / rd.y;
      float yb = (Y - ro.y) / rd.y;
      sy0 = max(sy0, min(ya, yb));
      sy1 = min(sy1, max(ya, yb));
    } else if (abs(ro.y) > Y) {
      sy1 = sy0;
    }
    if (sy1 > sy0) {
      // Steps crowd round the ray's closest approach to the nucleus, where
      // the sheet is densest: uniform in w = asinh((t - tc) / h).
      const int N = 16;
      vec3 qn = ro - uNucleus;
      float tcn = -dot(qn, rd);
      float bn2 = max(dot(qn, qn) - tcn * tcn, 0.0);
      float hw = max(sqrt(bn2), uNucR * 2.0);
      float w0 = asinh((sy0 - tcn) / hw);
      float w1 = asinh((sy1 - tcn) / hw);
      float dw = (w1 - w0) / float(N);
      vec3 acc = vec3(0.0);
      for (int i = 0; i < N; i++) {
        float ta = tcn + hw * sinh(w0 + dw * float(i));
        float tbb = tcn + hw * sinh(w0 + dw * float(i + 1));
        float dt = tbb - ta;
        float tm = tcn + hw * sinh(w0 + dw * (float(i) + 0.5));
        vec3 p = ro + rd * tm;
        vec3 q = p - uNucleus;
        float zz = q.z;
        float xx = -q.x;
        // Grains leave with a few hundred m/s in every direction, so the
        // sheet has a soft edge ahead of the nucleus and sunward of it
        // that widens with distance down the tail.
        float spread = uNucR * 1.5 + 0.04 * max(zz, 0.0);
        float edge = exp(-pow(min(xx, 0.0) / spread, 2.0) - pow(min(zz, 0.0) / (uNucR * 3.0), 2.0));
        if (edge < 1e-3) continue;
        // Back to (tau, beta) normalised to their maxima.
        float zs = max(zz, 0.0) + uNucR * 2.0;
        float xs = max(xx, 0.0) + uNucR * 0.5;
        float tauN = xs / (uDustK * zs);
        float betaN = zs / (uDustLen * tauN * tauN);
        // Size distribution: a soft upper cut at the most-pushed grains
        // (the sharp convex edge), and fewer big slow grains.
        float fBeta = pow(clamp(betaN, 0.0, 1.0), 0.35) * (1.0 - smoothstep(0.7, 1.15, betaN));
        // Emission history: more dust released recently, old dust faint.
        float lodB = (tm * uPixelAngle) / max(uDustK * zs / 15.0, 1e-4);
        float bands = ckFbm1(tauN * 15.0 + betaN * 2.0 + uSeed * 5.0, lodB);
        float striae = mix(1.0, 0.25 + 1.6 * bands * bands * 1.8, uStriae * smoothstep(0.05, 0.4, zz / uDustLen));
        float emit = exp(-1.6 * tauN) * (1.0 - smoothstep(0.8, 1.25, tauN)) * striae;
        // Surface density over the Jacobian k Ld^2 beta tau^4.
        float sigma = emit * fBeta * uDustK * zs / (uDustLen * xs * xs) * uNucR * edge;
        // Inside the dust coma the coma term carries the light.
        sigma *= smoothstep(uNucR * 2.0, uDustComaR * 1.5, length(q));
        // Fade the far end, and stretch the steep 1/z^2 fall the way a
        // long exposure does, so the fan reads to its end.
        sigma *= (1.0 - smoothstep(0.55, 1.0, zz / uDustLen)) * pow(zs / (2.0 * uNucR), 1.2);
        // Thickness grows with age (ejection speed times tau).
        float th = uDustThick0 + uDustThick1 * clamp(tauN, 0.0, 1.2);
        float yA = (ro.y + rd.y * ta) / th;
        float yB = (ro.y + rd.y * tbb) / th;
        float across = abs(yB - yA) > 1e-3
          ? abs(ckPhi(yB) - ckPhi(yA)) / max(abs(rd.y), 1e-6)
          : exp(-0.5 * yA * yA) * 0.3989 / th * dt;
        vec3 sunDir = normalize(sunPos - p);
        float phase = ckDustPhase(dot(sunDir, rd));
        // Inverse square solar flux across the tail.
        float flux = (uSunDist * uSunDist) / max(dot(p - sunPos, p - sunPos), 1e-3);
        vec3 tint = oklchMix(uDustHeadColor, uDustColor, smoothstep(0.0, 0.5, zz / uDustLen));
        acc += tint * sigma * across * phase * flux;
      }
      dustLight += acc * uDustGain;
    }
  }

  col += dustLight;
  col *= uExposure;
  if (max(col.r, max(col.g, col.b)) < 1e-5) discard;
  gl_FragColor = vec4(col, 1.0);
}
`;

const _v = new THREE.Vector3();
const _m = new THREE.Matrix4();

function srgbHexToLinear(hex) {
  return new THREE.Color(hex).convertSRGBToLinear();
}

// Shape of the comet at full activity, in scene units (s = size, L = tail length).
function cometMaxima(comet) {
  const s = comet.size;
  const L = comet.tailLength;
  return {
    ionLen: L * 4.0,
    dustLen: L * 3.2,
    dustK: Math.tan(THREE.MathUtils.degToRad(34)),
    gasR: s * 20,
    dustThick: s * 0.5 + L * 3.2 * 0.05
  };
}

export function createCometVolume(comet, palette) {
  const s = comet.size;
  const max = cometMaxima(comet);
  const xMin = -(max.dustK * max.dustLen * 1.1) - max.gasR;
  const xMax = max.gasR;
  const yHalf = Math.max(max.gasR, 3.2 * max.dustThick, s * 8);
  const zMin = -max.gasR;
  const zMax = Math.max(max.ionLen, max.dustLen) * 1.05;
  const boxMin = new THREE.Vector3(xMin, -yHalf, zMin);
  const boxMax = new THREE.Vector3(xMax, yHalf, zMax);
  const geometry = new THREE.BoxGeometry(xMax - xMin, 2 * yHalf, zMax - zMin);
  geometry.translate((xMax + xMin) / 2, 0, (zMax + zMin) / 2);

  const uniforms = {
    uCam: { value: new THREE.Vector3() },
    uBoxMin: { value: boxMin },
    uBoxMax: { value: boxMax },
    uNucleus: { value: new THREE.Vector3(0, 0, s * 0.52) },
    uNucR: { value: s * 0.18 },
    uSunDist: { value: 100 },
    uTime: { value: 0 },
    uPixelAngle: { value: 0.001 },
    uSeed: { value: (comet.name.length * 0.731) % 1 * 10 },
    uGasA: { value: s * 0.12 },
    uGasR: { value: s * 10 },
    uGasGain: { value: 1 },
    uDustComaA: { value: s * 0.06 },
    uDustComaR: { value: s * 5 },
    uDustComaGain: { value: 1 },
    uHood: { value: s * 2 },
    uJetGain: { value: 1 },
    uJetDir0: { value: new THREE.Vector3(-0.35, 0.25, -1).normalize() },
    uJetDir1: { value: new THREE.Vector3(0.45, -0.1, -1).normalize() },
    uJetDir2: { value: new THREE.Vector3(0.05, -0.55, -1).normalize() },
    uJetLen: { value: s * 2.5 },
    uIonLen: { value: comet.tailLength * 2 },
    uIonW0: { value: s * 0.3 },
    uIonW1: { value: s * 2 },
    uIonGain: { value: 1 },
    uDustLen: { value: comet.tailLength * 2 },
    uDustK: { value: 0.5 },
    uDustThick0: { value: s * 0.4 },
    uDustThick1: { value: comet.tailLength * 0.1 },
    uDustGain: { value: 1 },
    uStriae: { value: comet.striae != null ? comet.striae : 0.7 },
    uGasColor: { value: srgbHexToLinear(comet.gasColor || 0x6fe39a) },
    // CO+ fluorescence (the 420 nm comet-tail bands) photographs as a pale
    // electric blue rather than a deep royal one.
    uIonColor: { value: srgbHexToLinear(palette.ion).lerp(new THREE.Color(0.75, 0.85, 1.0), 0.2) },
    // Dust is sunlight off grains, slightly reddened: pale cream in the
    // tail, a little warmer at the head where the big grains are.
    uDustColor: { value: srgbHexToLinear(palette.dust).lerp(new THREE.Color(1, 0.97, 0.92), 0.45) },
    uDustHeadColor: { value: srgbHexToLinear(comet.dustHeadColor || 0xfff1d6) },
    uExposure: { value: 1 }
  };

  const material = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: VERT,
    fragmentShader: FRAG,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    // Back faces carry every ray, from outside the box or inside it, and the
    // depth test lets the nucleus (and anything in front) hide the volume.
    depthTest: true,
    side: THREE.BackSide,
    toneMapped: false
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'cometVolume';
  mesh.frustumCulled = false;
  mesh.renderOrder = 12;
  // Camera in the comet's frame and the pixel footprint, per render.
  mesh.onBeforeRender = (renderer, scene, camera) => {
    const parent = mesh.parent;
    if (!parent) return;
    _m.copy(parent.matrixWorld).invert();
    uniforms.uCam.value.copy(_v.setFromMatrixPosition(camera.matrixWorld)).applyMatrix4(_m);
    const c = uniforms.uCam.value;
    // Exposed for the subject: from inside the coma the inner coma is far
    // brighter per pixel than the whole head seen from afar, so the shot is
    // stopped down as the camera closes in (smooth in distance, no flicker).
    const dNuc = c.distanceTo(uniforms.uNucleus.value);
    const rg = uniforms.uGasR.value;
    const k = THREE.MathUtils.smoothstep(dNuc, 0.05 * rg, 0.8 * rg);
    uniforms.uExposure.value = 0.12 + 0.88 * k;
    const h = renderer.getDrawingBufferSize(_v).y || 1;
    const fov = camera.isPerspectiveCamera ? THREE.MathUtils.degToRad(camera.fov) : 1;
    uniforms.uPixelAngle.value = 2 * Math.tan(fov / 2) / h;
  };
  mesh.userData.cometVolume = true;
  return mesh;
}

// Per frame: activity 0..1 from the heating, r the distance from the Sun.
export function updateCometVolume(mesh, comet, { active, r, time, nucleusZ }) {
  const u = mesh.material.uniforms;
  const s = comet.size;
  const L = comet.tailLength;
  const a = THREE.MathUtils.clamp(active, 0, 1);
  const gasGain = comet.gasGain != null ? comet.gasGain : 1;
  const dustGain = comet.dustGain != null ? comet.dustGain : 1;
  const ionGain = comet.ionGain != null ? comet.ionGain : 1;
  u.uTime.value = time;
  u.uSunDist.value = r;
  u.uNucleus.value.set(0, 0, nucleusZ);
  // The coma grows and brightens as the comet heats up (production rate
  // rises steeply inside ~3 AU).
  u.uGasR.value = s * (8 + 12 * a);
  u.uGasA.value = s * (0.12 + 0.12 * a);
  u.uGasGain.value = gasGain * (0.08 + 0.5 * a);
  u.uDustComaR.value = s * (3 + 5 * a);
  u.uDustComaA.value = s * 0.05;
  u.uDustComaGain.value = dustGain * (0.15 + 0.9 * a);
  u.uHood.value = s * (0.9 + 1.6 * a);
  u.uJetGain.value = dustGain * (0.02 + 0.16 * a);
  u.uJetLen.value = s * (1.4 + 2.2 * a);
  // Tails: length and brightness rise with activity.
  u.uIonLen.value = L * (1.2 + 2.8 * a);
  u.uIonW0.value = s * 0.35;
  u.uIonW1.value = s * (2.0 + 4.0 * a);
  u.uIonGain.value = ionGain * (0.02 + 0.42 * a * a);
  u.uDustLen.value = L * (0.9 + 2.3 * a);
  u.uDustK.value = Math.tan(THREE.MathUtils.degToRad(22 + 12 * a));
  u.uDustThick0.value = s * 0.4;
  u.uDustThick1.value = L * (0.9 + 2.3 * a) * 0.05;
  u.uDustGain.value = dustGain * (0.3 + 6.0 * a * a);
}
